import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import ReactGA from 'react-ga4';
import addWatermark from '../pdf/watermark';
import logo from '../assets/images/finaculator1.png';

interface YearlyData {
  year: number;
  totalInvestment: number;
  interestGoal: number;
  
}

interface PDFGeneratorProps {
  yearlyData: YearlyData[];
  goalAmount: string;
  duration: string;
  rateOfReturn: string;
  totalInvestment: number | null;
  monthlySIP: number | null;
  isCalculated: boolean;
}

const PDFGenerator: React.FC<PDFGeneratorProps> = ({
  yearlyData,
  goalAmount,
  duration,
  rateOfReturn,
  totalInvestment,
  monthlySIP,
  isCalculated,
}) => {
  ReactGA.event({ 'category': 'Goal SIP PDF ', 'action': 'Goal SIP PDF Button', 'label': 'Goal SIP PDF' });
  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const generatePDF = () => {
    if (!isCalculated) {
      setSnackbarMessage("Please calculate the values before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
   

    if (!goalAmount || !duration || !rateOfReturn || totalInvestment === null || monthlySIP === null || yearlyData.length === 0) {
      setSnackbarMessage("Please ensure all fields are filled before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }

    console.log("Generating PDF with Currency Symbol:", currencySymbol);

    const watermarkWidth = 100;
    const watermarkHeight = 100;

    const doc = new jsPDF();

    doc.text("Goal SIP Investment Summary", 10, 10);
    const tableColumn = ["Parameter", "Value"];

    const tableRows = [
      ["Goal Amount", `${goalAmount.replace(/,/g, "")}`],
      ["Investment Duration (Years)", duration],
      ["Expected Rate of Return (%)", rateOfReturn],
      ["Total Investment", `${totalInvestment?.toFixed(2)}`],
      ["Monthly SIP Amount", `${monthlySIP?.toFixed(2)}`],
    ];

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      headStyles: { fillColor: [22, 160, 133] },
      theme: "grid",
    });

    const startYForYearlyData = (doc as any).autoTable.previous.finalY + 10;
    const yearlyTableColumn = ["Year", "Total Investment", "Interest Earned"];
    const yearlyTableRows = yearlyData.map(
      (data: YearlyData) => [
        data.year,
        `${data.totalInvestment.toFixed(2)}`,
        `${data.interestGoal.toFixed(2)}`,
      ]
    );

    autoTable(doc, {
      head: [yearlyTableColumn],
      body: yearlyTableRows,
      styles: { halign: 'center' },
      startY: startYForYearlyData,
      headStyles: { fillColor: [22, 160, 133] },
      theme: "grid",
    });

    addWatermark(doc, logo, watermarkWidth, watermarkHeight);
    const img = new Image();
    img.src = logo;
    img.onload = () => {
    const now = new Date();
    const padZero = (num: string | number) => ("0" + num).slice(-2);
    const formattedDate = `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
    const formattedTime = `${padZero(now.getHours())}-${padZero(now.getMinutes())}-${padZero(now.getSeconds())}`;
    const timestamp = `${formattedDate}_${formattedTime}`;
    const fileName = `finaculator_goalsip_calculator_${timestamp}.pdf`;
    doc.save(fileName);
  };
};

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="success"
        onClick={generatePDF}
        style={{ marginTop: -70 }}
        startIcon={<DownloadIcon />}
        
      >
        Download PDF
      </Button>

      {/* Snackbar for validation alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Adjust position here
      >
        <MuiAlert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

    </div>
  );
};

export default PDFGenerator;
